// src/pages/AdminDashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import API from "../services/api";
import "leaflet/dist/leaflet.css";
import "./AdminDashboard.css";
import AdminDashboardCharts from "./AdminDashboardCharts";
import MapApiStatusBadge from "./MapApiStatusBadge";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

const POLL_INTERVAL_MS = 30000;
const ADDR_GEO_CACHE_KEY = "addressGeoCache";
const MAP_RECENT_LIMIT = 5;

const formatIST = (isoOrDateLike) => {
  if (!isoOrDateLike) return "";
  const d = new Date(isoOrDateLike);
  if (isNaN(d.getTime())) return String(isoOrDateLike);
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const normalizePoint = (raw) => {
  if (!raw) return null;

  const toPoint = (latVal, lngVal) => {
    const lat = Number(latVal);
    const lng = Number(lngVal);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return null;
  };

  // "28.61,77.23"
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return null;
    if (s.startsWith("{") || s.startsWith("[")) {
      try {
        return normalizePoint(JSON.parse(s));
      } catch {
        return null;
      }
    }
    const m = s.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
    if (m) return toPoint(m[1], m[2]);
    return null;
  }

  // [lng, lat] or [lat, lng] style
  if (Array.isArray(raw) && raw.length >= 2) {
    const a = Number(raw[0]);
    const b = Number(raw[1]);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      // Prefer GeoJSON [lng,lat]
      if (Math.abs(a) <= 180 && Math.abs(b) <= 90) return { lat: b, lng: a };
      if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lng: b };
    }
    return null;
  }

  // Object with common shapes
  if (typeof raw === "object") {
    // GeoJSON: { type: "Point", coordinates: [lng, lat] }
    if (Array.isArray(raw.coordinates)) {
      const geo = normalizePoint(raw.coordinates);
      if (geo) return geo;
    }
    const direct = toPoint(
      raw.lat ?? raw.latitude ?? raw.y,
      raw.lng ?? raw.lon ?? raw.longitude ?? raw.x
    );
    if (direct) return direct;
  }

  return null;
};

const initialsFor = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "NA";

const AdminDashboard = () => {
  const [vendorRequirements, setVendorRequirements] = useState([]);
  const [supplierDispatches, setSupplierDispatches] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ name: "", mobile: "" });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [search, setSearch] = useState("");

  // ---------- Reverse Geo Cache ----------
  const [placeCache, setPlaceCache] = useState(() => {
    try {
      const raw = localStorage.getItem("reverseGeoCache");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const [addressGeoCache, setAddressGeoCache] = useState(() => {
    try {
      const raw = localStorage.getItem(ADDR_GEO_CACHE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const geocodeQueueRef = useRef([]);
  const geocodeTimerRef = useRef(null);

  const vendorPinIcon = useMemo(
    () =>
      L.divIcon({
        className: "adash-pin-wrap",
        html: '<span class="adash-pin" style="--pin:#137fec"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -8],
      }),
    []
  );
  const supplierPinIcon = useMemo(
    () =>
      L.divIcon({
        className: "adash-pin-wrap",
        html: '<span class="adash-pin" style="--pin:#ef4444"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -8],
      }),
    []
  );
  const employeePinIcon = useMemo(
    () =>
      L.divIcon({
        className: "adash-pin-wrap",
        html: '<span class="adash-pin" style="--pin:#22c55e"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -8],
      }),
    []
  );

  const persistCache = (next) => {
    setPlaceCache(next);
    try {
      localStorage.setItem("reverseGeoCache", JSON.stringify(next));
    } catch {}
  };

  const cacheKey = (lat, lng) =>
    `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
  const addrKey = (addr = "") => String(addr || "").trim().toLowerCase();

  const enqueueReverseGeo = (lat, lng) => {
    const key = cacheKey(lat, lng);
    if (placeCache[key]) return;
    if (!queueRef.current.find((q) => q.key === key)) {
      queueRef.current.push({ key, lat, lng });
    }
    runQueue();
  };

  const runQueue = () => {
    if (timerRef.current) return;
    const tick = async () => {
      const job = queueRef.current.shift();
      if (!job) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      try {
        const name = await reverseGeocode(job.lat, job.lng);
        persistCache({ ...placeCache, [job.key]: name });
      } catch {
        persistCache({ ...placeCache, [job.key]: "Unknown place" });
      }
    };
    timerRef.current = setInterval(tick, 1100);
  };

  const reverseGeocode = async (lat, lng) => {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
      lat
    )}&lon=${encodeURIComponent(lng)}&zoom=14&addressdetails=1`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("reverse geocode failed");
    const json = await res.json();
    if (json.display_name) return json.display_name;
    const a = json.address || {};
    return [a.road, a.suburb, a.city || a.town || a.village, a.state]
      .filter(Boolean)
      .join(", ") || "Unknown place";
  };

  const persistAddressGeoCache = (next) => {
    setAddressGeoCache(next);
    try {
      localStorage.setItem(ADDR_GEO_CACHE_KEY, JSON.stringify(next));
    } catch {}
  };

  const geocodeAddress = async (address) => {
    const q = encodeURIComponent(address);
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${q}&limit=1`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("address geocode failed");
    const rows = await res.json();
    const first = Array.isArray(rows) ? rows[0] : null;
    const lat = Number(first?.lat);
    const lng = Number(first?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error("no lat/lng for address");
    return { lat, lng };
  };

  const runGeocodeQueue = () => {
    if (geocodeTimerRef.current) return;
    const tick = async () => {
      const job = geocodeQueueRef.current.shift();
      if (!job) {
        clearInterval(geocodeTimerRef.current);
        geocodeTimerRef.current = null;
        return;
      }
      try {
        const point = await geocodeAddress(job.address);
        persistAddressGeoCache({ ...addressGeoCache, [job.key]: point });
      } catch {
        persistAddressGeoCache({ ...addressGeoCache, [job.key]: null });
      }
    };
    geocodeTimerRef.current = setInterval(tick, 1200);
  };

  const enqueueAddressGeocode = (address) => {
    const key = addrKey(address);
    if (!key) return;
    if (Object.prototype.hasOwnProperty.call(addressGeoCache, key)) return;
    if (!geocodeQueueRef.current.find((q) => q.key === key)) {
      geocodeQueueRef.current.push({ key, address });
    }
    runGeocodeQueue();
  };

  // ---------- API calls ----------
  const fetchAttendance = async () => {
    try {
      const { data } = await API.get("/admin/attendance-summary/today");

      const marked = (data || [])
        .filter((entry) => String(entry.status).toLowerCase() !== "absent")
        .map((entry) => {
          let parsedLocation = null;
          try {
            parsedLocation =
              typeof entry.location === "string"
                ? JSON.parse(entry.location)
                : entry.location;
            if (parsedLocation) {
              parsedLocation = {
                lat: Number(parsedLocation.lat ?? parsedLocation.latitude),
                lng: Number(parsedLocation.lng ?? parsedLocation.longitude),
              };
              if (!Number.isFinite(parsedLocation.lat) || !Number.isFinite(parsedLocation.lng)) {
                parsedLocation = null;
              }
            }
          } catch {
            parsedLocation = null;
          }

          if (!parsedLocation) {
            const lat = Number(entry?.lat ?? entry?.latitude);
            const lng = Number(entry?.lng ?? entry?.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              parsedLocation = { lat, lng };
            }
          }

          return {
            ...entry,
            status: entry.status,
            location: parsedLocation || null,
            punchAt: entry.timestamp || entry.date || null,
            punchInTime: entry.punchInTime || null,
            punchOutTime: entry.punchOutTime || null,
            punchTimeText: entry.time || entry.punchInTime || null,
          };
        });

      setAttendanceData(marked);
      setLastUpdated(new Date().toISOString());

      marked.forEach((e) => {
        const lat = Number(e?.location?.lat);
        const lng = Number(e?.location?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) enqueueReverseGeo(lat, lng);
      });
    } catch (err) {
      console.error("Error fetching attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequirements = async () => {
    try {
      const res = await API.get("/admin/vendor-requirements");

      const enriched = (res.data || []).map((r) => {
        const reqLoc = normalizePoint(r.location);
        const vendorLoc = normalizePoint(r?.vendorId?.geoLocation || r?.vendorId?.location);
        const textLoc = normalizePoint(r?.locationText);
        const mapLocation = reqLoc || vendorLoc || textLoc || null;
        const geoAddress =
          r?.locationText ||
          r?.location?.address ||
          r?.vendor?.address ||
          r?.vendorId?.address ||
          "";
        const geoKey = addrKey(geoAddress);
        if (!mapLocation && geoKey) enqueueAddressGeocode(geoAddress);
        return {
          ...r,
          _mapLocation: mapLocation,
          _geoAddressKey: geoKey,
          _geoAddressText: geoAddress,
          _vendorName:
            r.vendor?.companyName ||
            r.vendorName ||
            r?.vendorId?.companyName ||
            r?.vendorId?.userId?.fullName ||
            "Vendor",
        };
      });

      const sorted = enriched.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setVendorRequirements(sorted);
    } catch (err) {
      console.error("Error fetching vendor requirements:", err);
    }
  };

  const fetchSupplierDispatches = async () => {
    try {
      const res = await API.get("/admin/supplier-dispatches");

      const mapped = (res.data || []).map((d) => {
        const knownLoc =
          normalizePoint(d.location) ||
          normalizePoint(d.pickupLocation) ||
          normalizePoint(d.geoLocation) ||
          normalizePoint(d.pickupAddress) ||
          normalizePoint(d?.supplierProfile?.address);
        const fallbackAddress =
          d.pickupAddress ||
          d.supplierProfile?.address ||
          d.companyAddress ||
          "";
        const key = addrKey(fallbackAddress);
        if (!knownLoc && key) enqueueAddressGeocode(fallbackAddress);
        return {
          ...d,
          _mapLocation: knownLoc,
          _geoAddressKey: key,
          _geoAddressText: fallbackAddress,
        };
      });
      setSupplierDispatches(mapped);
    } catch (err) {
      console.error("Error fetching supplier dispatches:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await API.get("/admin/employees");
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await API.get("/admin/vendors");
      setVendors(res.data || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };

  useEffect(() => {
    fetchAttendance();
    fetchRequirements();
    fetchSupplierDispatches();
    fetchEmployees();
    fetchVendors();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (geocodeTimerRef.current) clearInterval(geocodeTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      fetchAttendance();
      fetchRequirements();
      fetchSupplierDispatches();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAttendance = useMemo(() => {
    return attendanceData.filter((entry) => {
      const name = entry?.fullName || entry?.employee?.fullName || "";
      const mobile = entry?.mobile || entry?.employee?.mobile || "";
      const searchText = `${name} ${mobile}`.toLowerCase();
      return (
        (!search.trim() || searchText.includes(search.trim().toLowerCase())) &&
        name.toLowerCase().includes(filter.name.toLowerCase()) &&
        mobile.toLowerCase().includes(filter.mobile.toLowerCase())
      );
    });
  }, [attendanceData, filter, search]);

  const filteredVendorRequirements = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = vendorRequirements || [];
    if (!q) return rows;
    return rows.filter((req) => {
      const haystack = [
        req?._vendorName,
        req?.vendorName,
        req?.productType,
        req?.approvalStatus,
        req?._geoAddressText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [vendorRequirements, search]);

  const presentCount = filteredAttendance.length;
  const totalEmployees = employees.length;
  const totalVendors = vendors.length;
  const absentCount = Math.max(0, totalEmployees - presentCount);

  const placeNameFor = (loc) => {
    const lat = Number(loc?.lat);
    const lng = Number(loc?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
    const key = cacheKey(lat, lng);
    return placeCache[key] || "";
  };

  const displayPunch = (entry) => {
    const pin = entry.punchInTime || "";
    const pout = entry.punchOutTime || "";
    if (pin || pout) {
      return `In: ${pin || "-"} | Out: ${pout || "-"}`;
    }
    return entry.punchTimeText || formatIST(entry.punchAt) || "";
  };

  const statusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s.includes("present")) return "present";
    if (s.includes("half")) return "half";
    if (s.includes("absent")) return "absent";
    return "half";
  };

  const hasValidPoint = (p) =>
    Number.isFinite(Number(p?.lat)) && Number.isFinite(Number(p?.lng));

  const recentVendorPins = useMemo(() => {
    return (vendorRequirements || [])
      .slice(0, MAP_RECENT_LIMIT)
      .map((req) => {
        const fallback = req?._geoAddressKey ? addressGeoCache[req._geoAddressKey] : null;
        const point = req?._mapLocation || fallback;
        return {
          req,
          point: hasValidPoint(point)
            ? { lat: Number(point.lat), lng: Number(point.lng) }
            : null,
        };
      })
      .filter((x) => hasValidPoint(x.point));
  }, [vendorRequirements, addressGeoCache]);

  const recentDispatchPins = useMemo(() => {
    const sorted = [...(supplierDispatches || [])].sort(
      (a, b) =>
        new Date(b.submittedAt || b.createdAt || 0) -
        new Date(a.submittedAt || a.createdAt || 0)
    );
    return sorted
      .slice(0, MAP_RECENT_LIMIT)
      .map((d) => {
        const fallback = d?._geoAddressKey ? addressGeoCache[d._geoAddressKey] : null;
        const point = d?._mapLocation || fallback;
        return {
          d,
          point: hasValidPoint(point)
            ? { lat: Number(point.lat), lng: Number(point.lng) }
            : null,
        };
      })
      .filter((x) => hasValidPoint(x.point));
  }, [supplierDispatches, addressGeoCache]);

  const recentAttendancePins = useMemo(() => {
    const sorted = [...(attendanceData || [])].sort(
      (a, b) =>
        new Date(b.punchAt || b.timestamp || b.createdAt || 0) -
        new Date(a.punchAt || a.timestamp || a.createdAt || 0)
    );
    return sorted
      .filter((entry) => hasValidPoint(entry?.location))
      .slice(0, MAP_RECENT_LIMIT);
  }, [attendanceData]);

  const alerts = useMemo(() => {
    const list = [];
    const pending = (vendorRequirements || []).filter((r) =>
      String(r.approvalStatus || "").toLowerCase().includes("pending")
    );
    const unassigned = (vendorRequirements || []).filter((r) => !r.assignedEmployee);

    if (pending.length) {
      list.push({
        tone: "warn",
        title: "Pending Requirements",
        text: `${pending.length} requirement(s) awaiting approval.`,
        time: lastUpdated ? formatIST(lastUpdated) : "Just now",
      });
    }

    if (unassigned.length) {
      list.push({
        tone: "danger",
        title: "Unassigned Requirements",
        text: `${unassigned.length} requirement(s) not assigned to any employee.`,
        time: lastUpdated ? formatIST(lastUpdated) : "Just now",
      });
    }

    if (absentCount > 0) {
      list.push({
        tone: "info",
        title: "Absent Today",
        text: `${absentCount} employee(s) absent today.`,
        time: lastUpdated ? formatIST(lastUpdated) : "Today",
      });
    }

    return list;
  }, [vendorRequirements, absentCount, lastUpdated]);

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    []
  );

  const statCards = [
    {
      label: "Total Employees",
      value: totalEmployees,
      icon: "groups",
      tone: "blue",
      change: "+4% from last week",
      changeIcon: "trending_up",
    },
    {
      label: "Active Vendors",
      value: totalVendors,
      icon: "storefront",
      tone: "green",
      change: "Steady",
      changeIcon: "horizontal_rule",
    },
    {
      label: "Present Today",
      value: presentCount,
      icon: "how_to_reg",
      tone: "indigo",
      change: totalEmployees ? `${Math.round((presentCount / totalEmployees) * 100)}% attendance rate` : "0% attendance rate",
      changeIcon: "trending_up",
    },
    {
      label: "Absent / Off",
      value: absentCount,
      icon: "person_off",
      tone: "red",
      change: absentCount > 0 ? `${absentCount} needs attention` : "All staffed",
      changeIcon: absentCount > 0 ? "trending_up" : "check_circle",
    },
  ];

  const quickActions = [
    { label: "Employees", value: totalEmployees, icon: "badge", tone: "blue" },
    { label: "Vendors", value: totalVendors, icon: "store", tone: "green" },
    { label: "Pending Req.", value: (vendorRequirements || []).filter((r) => String(r?.approvalStatus || "").toLowerCase().includes("pending")).length, icon: "inventory", tone: "amber" },
    { label: "Live Pins", value: recentAttendancePins.length + recentVendorPins.length + recentDispatchPins.length, icon: "location_on", tone: "indigo" },
  ];

  return (
    <div className="adash-page">
      <div className="adash-container">
        <div className="adash-header">
          <div className="adash-heading">
            <div className="adash-eyebrow">Lotus Rice Mill</div>
            <div className="adash-title">Field Operations Dashboard</div>
            <div className="adash-subtitle">Management overview for today, {todayLabel}</div>
            <div className="adash-summary-strip">
              <div className="adash-summary-item">
                <span className="material-symbols-outlined">schedule</span>
                Auto-refresh every 30 seconds
              </div>
              <div className="adash-summary-item">
                <span className="material-symbols-outlined">task_alt</span>
                {presentCount} active attendance logs today
              </div>
              <div className="adash-summary-item">
                <span className="material-symbols-outlined">warning</span>
                {alerts.length} alert{alerts.length === 1 ? "" : "s"} need review
              </div>
            </div>
          </div>
          <div className="adash-header-actions">
            <div className="adash-search">
              <span className="material-symbols-outlined icon">search</span>
              <input
                type="text"
                placeholder="Search employees, vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="adash-quick-grid">
          {quickActions.map((item) => (
            <div key={item.label} className={`adash-quick-card tone-${item.tone}`}>
              <div className="adash-quick-icon">
                <span className="material-symbols-outlined">{item.icon}</span>
              </div>
              <div>
                <div className="adash-quick-value">{item.value}</div>
                <div className="adash-quick-label">{item.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="adash-grid stats">
          {statCards.map((card) => (
            <div key={card.label} className={`adash-stat tone-${card.tone}`}>
              <div className="adash-stat-top">
                <div className="adash-stat-label">{card.label}</div>
                <div className="adash-stat-icon">
                  <span className="material-symbols-outlined">{card.icon}</span>
                </div>
              </div>
              <div className="adash-stat-value">{card.value}</div>
              <div className="adash-stat-change">
                <span className="material-symbols-outlined">{card.changeIcon}</span>
                {card.change}
              </div>
            </div>
          ))}
        </div>

        <AdminDashboardCharts
          attendanceData={attendanceData}
          vendorRequirements={vendorRequirements}
          stats={{ totalEmployees, totalVendors, presentCount, absentCount }}
        />

        <div className="adash-grid map-alerts">
          <div className="adash-card">
            <div className="adash-card-header">
              <div>
                <div className="adash-card-title">Real-time Operations Map</div>
                <div className="adash-subtitle">Live field visibility across attendance, vendor demand, and dispatches</div>
              </div>
              <div className="adash-map-legend">
                <span className="adash-dot" style={{ background: "#22c55e" }} /> Employees
                <span className="adash-dot" style={{ background: "#137fec" }} /> Vendors
                <span className="adash-dot" style={{ background: "#ef4444" }} /> Dispatches
              </div>
            </div>
            <div className="adash-card-body">
              {lastUpdated && (
                <div className="adash-subtitle" style={{ marginBottom: 8 }}>
                  Last updated: {formatIST(lastUpdated)}
                </div>
              )}
              <div className="adash-subtitle" style={{ marginBottom: 8 }}>
                Showing recent {MAP_RECENT_LIMIT}: Vendors {recentVendorPins.length} | Dispatch{" "}
                {recentDispatchPins.length} | Attendance {recentAttendancePins.length}
              </div>
              <MapApiStatusBadge />
              <div className="adash-map">
                <MapContainer
                  center={[28.61, 77.23]}
                  zoom={5}
                  style={{ height: "100%", width: "100%" }}
                  className="w-100"
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                  {recentVendorPins.map(({ req, point }, idx) =>
                    hasValidPoint(point) ? (
                      <Marker
                        key={`vendor-${idx}`}
                        position={[Number(point.lat), Number(point.lng)]}
                        icon={vendorPinIcon}
                      >
                        <Popup>
                          <b>Vendor:</b> {req._vendorName} <br />
                          <b>Product:</b> {req.productType} <br />
                          <b>Qty:</b> {req.quantity} {req.unit} <br />
                          <b>Status:</b> {req.approvalStatus}
                        </Popup>
                      </Marker>
                    ) : null
                  )}

                  {recentDispatchPins.map(({ d, point }, idx) => {
                    return hasValidPoint(point) ? (
                      <Marker
                        key={`dispatch-${idx}`}
                        position={[Number(point.lat), Number(point.lng)]}
                        icon={supplierPinIcon}
                      >
                        <Popup>
                          <b>Supplier:</b> {d.companyName || d.supplierName || "Supplier"} <br />
                          <b>Material:</b> {d.materialName || "-"} <br />
                          <b>Qty:</b> {d.quantity || "-"} {d.unit || ""} <br />
                          <b>Status:</b> {d.approvalStatus || "draft"} <br />
                          <b>Pickup:</b> {d.pickupAddress || "N/A"}
                        </Popup>
                      </Marker>
                    ) : null;
                  })}

                  {recentAttendancePins.map((entry, idx) =>
                    hasValidPoint(entry?.location) ? (
                      <Marker
                        key={`att-${idx}`}
                        position={[Number(entry.location.lat), Number(entry.location.lng)]}
                        icon={employeePinIcon}
                      >
                        <Popup>
                          <b>{entry?.fullName || "N/A"}</b> <br />
                          <b>📞</b>{" "}
                          {entry?.mobile ? (
                            <a href={`tel:${entry.mobile}`}>{entry.mobile}</a>
                          ) : (
                            "N/A"
                          )}
                          <br />
                          <b>Status:</b> {entry.status} <br />
                          <b>Punch:</b> {displayPunch(entry)} <br />
                          <b>Location:</b>{" "}
                          {placeNameFor(entry.location) || "Resolving..."} <br />
                          {entry.selfie && (
                            <img
                              src={entry.selfie}
                              alt="Selfie"
                              width="80"
                              className="mt-1 rounded"
                            />
                          )}
                        </Popup>
                      </Marker>
                    ) : null
                  )}
                </MapContainer>
              </div>
            </div>
          </div>

          <div className="adash-card">
            <div className="adash-card-header">
              <div>
                <div className="adash-card-title">Active Alerts</div>
                <div className="adash-subtitle">Operational exceptions detected from today&apos;s live data</div>
              </div>
            </div>
            <div className="adash-card-body adash-alerts-body">
              {alerts.map((a, i) => (
                <div key={i} className={`adash-alert-item ${a.tone || ""}`}>
                  <span className="material-symbols-outlined">
                    {a.tone === "danger" ? "error" : a.tone === "warn" ? "warning" : "info"}
                  </span>
                  <div>
                    <div className="adash-alert-title">{a.title}</div>
                    <div className="adash-alert-text">{a.text}</div>
                    <div className="adash-alert-time">{a.time}</div>
                  </div>
                </div>
              ))}
              {alerts.length === 0 && (
                <div className="adash-subtitle">No alerts right now</div>
              )}
            </div>
            <button className="adash-text-action" type="button">VIEW ALL ALERTS</button>
          </div>
        </div>

        <div className="adash-grid records">
          <div className="adash-card">
            <div className="adash-card-header">
              <div>
                <div className="adash-card-title">Today&apos;s Attendance</div>
                <div className="adash-subtitle">
                  {lastUpdated ? `Last updated: ${formatIST(lastUpdated)}` : "Awaiting attendance feed"}
                </div>
              </div>
              <div className="adash-inline-actions">
                <button className="adash-inline-pill" type="button">Present {presentCount}</button>
                <button className="adash-link-btn" type="button">Download Log</button>
              </div>
            </div>
            <div className="adash-card-body">
              <div className="adash-filter-row" style={{ marginBottom: 12 }}>
                <input
                  className="adash-input"
                  type="text"
                  placeholder="Search by Name"
                  value={filter.name}
                  onChange={(e) => setFilter({ ...filter, name: e.target.value })}
                />
                <input
                  className="adash-input"
                  type="text"
                  placeholder="Search by Mobile"
                  value={filter.mobile}
                  onChange={(e) => setFilter({ ...filter, mobile: e.target.value })}
                />
              </div>

              {loading ? (
                <div className="adash-subtitle">Loading…</div>
              ) : filteredAttendance.length === 0 ? (
                <div className="adash-subtitle">No attendance marked yet</div>
              ) : (
                <>
                  <div className="adash-table-wrap">
                    <table className="adash-table">
                      <thead>
                        <tr>
                          <th>Employee Name</th>
                          <th>Status</th>
                          <th>Punch In</th>
                          <th>Punch Out</th>
                          <th>Location</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAttendance.map((entry, idx) => {
                          const place = entry?.location && placeNameFor(entry.location);
                          const hasCoords = entry?.location?.lat && entry?.location?.lng;

                          return (
                            <tr key={idx}>
                              <td>
                                <div className="adash-person-cell">
                                  <div className="adash-avatar">{initialsFor(entry?.fullName)}</div>
                                  <div>
                                    <div className="adash-person-name">{entry?.fullName || "N/A"}</div>
                                    <div className="adash-person-sub">{entry?.mobile || "N/A"}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className={`adash-pill ${statusClass(entry.status)}`}>
                                  {entry.status}
                                </span>
                              </td>
                              <td>{entry.punchInTime || "-"}</td>
                              <td>{entry.punchOutTime || "-"}</td>
                              <td>
                                {hasCoords ? (
                                  <>
                                    {place || "Resolving..."}{" "}
                                    <a
                                      href={`https://www.google.com/maps?q=${entry.location.lat},${entry.location.lng}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      📍
                                    </a>
                                  </>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>
                                {hasCoords ? (
                                  <a
                                    href={`https://www.google.com/maps?q=${entry.location.lat},${entry.location.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="adash-table-link"
                                  >
                                    View Map
                                  </a>
                                ) : (
                                  <span className="adash-muted">Map unavailable</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="adash-cards">
                    {filteredAttendance.map((entry, idx) => {
                      const place = entry?.location && placeNameFor(entry.location);
                      const hasCoords = entry?.location?.lat && entry?.location?.lng;
                      return (
                        <div key={idx} className="adash-att-card">
                          <div className="adash-att-top">
                            <div className="adash-person-cell">
                              <div className="adash-avatar">{initialsFor(entry?.fullName)}</div>
                              <div>
                                <div className="adash-att-name">{entry?.fullName || "N/A"}</div>
                                <div className="adash-att-sub">Mobile: {entry?.mobile || "N/A"}</div>
                              </div>
                            </div>
                            <span className={`adash-pill ${statusClass(entry.status)}`}>
                              {entry.status}
                            </span>
                          </div>
                          <div className="adash-att-row">
                            <span className="adash-att-label">Punch In</span>
                            <span className="adash-att-value">{entry.punchInTime || "-"}</span>
                          </div>
                          <div className="adash-att-row">
                            <span className="adash-att-label">Punch Out</span>
                            <span className="adash-att-value">{entry.punchOutTime || "-"}</span>
                          </div>
                          <div className="adash-att-row">
                            <span className="adash-att-label">Location</span>
                            <span className="adash-att-value">
                              {hasCoords ? (
                                <>
                                  {place || "Resolving..."}{" "}
                                  <a
                                    href={`https://www.google.com/maps?q=${entry.location.lat},${entry.location.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    📍
                                  </a>
                                </>
                              ) : (
                                "N/A"
                              )}
                            </span>
                          </div>
                          <div className="adash-att-actions">
                            {hasCoords ? (
                              <a
                                href={`https://www.google.com/maps?q=${entry.location.lat},${entry.location.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View on Map
                              </a>
                            ) : (
                              <span className="adash-muted">Map unavailable</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="adash-card-footer">
              <button className="adash-footer-btn" type="button">
                Show All Records
                <span className="material-symbols-outlined">expand_more</span>
              </button>
            </div>
          </div>

          <div className="adash-card">
            <div className="adash-card-header">
              <div>
                <div className="adash-card-title">Vendor Requirements</div>
                <div className="adash-subtitle">Most recent requests requiring planning or review</div>
              </div>
              <span className="material-symbols-outlined adash-muted">more_vert</span>
            </div>
            <div className="adash-card-body adash-requirements-list">
              {filteredVendorRequirements.slice(0, 5).map((req, idx) => {
                const tone = String(req?.approvalStatus || "").toLowerCase();
                const badgeClass = tone.includes("complete")
                  ? "done"
                  : tone.includes("pending")
                    ? "urgent"
                    : "standard";
                return (
                  <div key={req?._id || idx} className="adash-req-item">
                    <div className="adash-req-top">
                      <span className={`adash-req-badge ${badgeClass}`}>
                        {req?.approvalStatus || "Standard Request"}
                      </span>
                      <span className="adash-req-time">{formatIST(req?.createdAt || req?.updatedAt)}</span>
                    </div>
                    <div className="adash-req-name">{req?._vendorName || "Vendor"}</div>
                    <div className="adash-req-text">
                      {req?.productType || "Product"} request for {req?.quantity ?? "-"} {req?.unit || ""}.
                    </div>
                  </div>
                );
              })}
              {filteredVendorRequirements.length === 0 && (
                <div className="adash-subtitle">No vendor requirements available.</div>
              )}
            </div>
            <div className="adash-card-footer">
              <button className="adash-link-btn" type="button">View All Requests</button>
            </div>
          </div>
        </div>

        <div className="adash-footer">
          © 2024 FieldOps Management System • Operational Integrity Guaranteed
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
